/**
 * Project Users Component
 *
 * Manages user access to a specific project.
 * Allows admins to:
 * - View users with access to the project
 * - Add new users to the project
 * - Update user roles
 * - Remove users from the project
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Stack,
  alpha,
  useTheme,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewerIcon,
  Assessment as AnalystIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  CheckCircle as ActiveIcon,
  Create as EditorIcon,
  PublicOutlined as ExternalIcon,
  Send as SendIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface ProjectUser {
  access_id: string;
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_default_project: boolean;
  is_external_collaborator?: boolean;
  granted_at: string;
  granted_by?: {
    name: string;
    email: string;
  };
  last_accessed_at?: string;
  password_set_at?: string;
  invitation_status?: string;
  invitation_sent_at?: string;
}

// App User from /users endpoint
interface AppUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
}

interface ProjectUsersProps {
  projectId: string;
  projectName: string;
  onBack?: () => void;
}

// Roles matching ADMIN_PORTAL_FEATURE_FLAGS_SCHEMA.md
const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full access to all features', color: 'error' as const },
  { value: 'analyst', label: 'Analyst', description: 'Can view and analyze data, create reports', color: 'info' as const },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to dashboards and reports', color: 'success' as const },
  { value: 'editor', label: 'Editor', description: 'Can upload data and create maps', color: 'warning' as const },
];

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'admin':
      return <AdminIcon fontSize="small" />;
    case 'analyst':
      return <AnalystIcon fontSize="small" />;
    case 'viewer':
      return <ViewerIcon fontSize="small" />;
    case 'editor':
      return <EditorIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

// Max App Users fetched per selector search (backend caps /users at 500)
const APP_USER_PAGE_SIZE = 100;

const getRoleColor = (role: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
  const roleConfig = ROLES.find(r => r.value === role);
  return roleConfig?.color || 'default';
};

// Get initials from name or email
const getInitials = (name: string, email: string): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
};

// Get avatar background color based on string
const getAvatarColor = (str: string): string => {
  const colors = [
    '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
    '#0288d1', '#f57c00', '#455a64', '#00796b',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ProjectUsers: React.FC<ProjectUsersProps> = ({
  projectId,
  projectName,
  onBack,
}) => {
  const theme = useTheme();

  // State
  const [users, setUsers] = useState<ProjectUser[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  // One server page of App Users matching the picker's search
  const [appUserPage, setAppUserPage] = useState<AppUser[]>([]);
  const [loadingAppUsers, setLoadingAppUsers] = useState(false);
  const [appUserSearch, setAppUserSearch] = useState('');
  const [appUserMatchCount, setAppUserMatchCount] = useState(0);
  const [appUserSearchError, setAppUserSearchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProjectUser | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    role: 'viewer',
    is_default_project: false,
  });
  const [selectedAppUser, setSelectedAppUser] = useState<AppUser | null>(null);

  // Load project users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/projects/${projectId}/users`);
      setUsers(response.data.users || []);
      setUsersLoaded(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load project users');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Search App Users for the selector. /users is paginated, so the search runs
  // server-side: filtering a single page client-side hides everyone past it.
  // Waits for the project's own users so they are never offered as addable.
  useEffect(() => {
    if (!addDialogOpen || !usersLoaded) return;

    let cancelled = false;
    setLoadingAppUsers(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/users', {
          params: { search: appUserSearch.trim() || undefined, limit: APP_USER_PAGE_SIZE },
        });
        if (cancelled) return;
        const page: AppUser[] = response.data.users || [];
        setAppUserPage(page);
        setAppUserMatchCount(response.data.total ?? page.length);
        setAppUserSearchError(null);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to load app users:', err);
        setAppUserPage([]);
        setAppUserMatchCount(0);
        setAppUserSearchError(err.response?.data?.detail || 'Could not load users');
      } finally {
        if (!cancelled) setLoadingAppUsers(false);
      }
    }, appUserSearch ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addDialogOpen, usersLoaded, appUserSearch]);

  // Users already in this project are filtered out here, not on the server,
  // so `appUserMatchCount` still counts them
  const availableAppUsers = useMemo(() => {
    const existingEmails = new Set(users.map(u => u.email.toLowerCase()));
    return appUserPage.filter(u => !existingEmails.has(u.email.toLowerCase()));
  }, [appUserPage, users]);

  // Keep the picked user among the options so a later search doesn't orphan it
  const appUserOptions =
    selectedAppUser && !availableAppUsers.some(u => u.id === selectedAppUser.id)
      ? [selectedAppUser, ...availableAppUsers]
      : availableAppUsers;

  const appUserPageTruncated = appUserMatchCount > appUserPage.length;

  const appUserHelperText = (() => {
    if (!usersLoaded) {
      return loading
        ? "Waiting for this project's current users to load..."
        : "Could not load this project's current users. Close and reopen to try again.";
    }
    if (loadingAppUsers) return 'Searching...';
    if (appUserSearchError) return `Could not search users: ${appUserSearchError}. Close and reopen to try again.`;
    if (appUserPageTruncated) {
      if (availableAppUsers.length === 0) {
        return `The first ${appUserPage.length} matches already have access. Type a name or email to find others.`;
      }
      return appUserSearch.trim()
        ? `Only the first ${appUserPage.length} of ${appUserMatchCount} matches were searched. Keep typing to narrow.`
        : `${appUserMatchCount} users in total. Type a name or email to search them all.`;
    }
    if (availableAppUsers.length === 0) {
      return appUserSearch.trim()
        ? 'No matching users without access to this project.'
        : 'No available users. All users may already have access.';
    }
    return `${availableAppUsers.length} user${availableAppUsers.length !== 1 ? 's' : ''} available`;
  })();

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      role: 'viewer',
      is_default_project: false,
    });
    setSelectedUser(null);
    setSelectedAppUser(null);
  };

  // Open add dialog
  const handleOpenAdd = () => {
    resetForm();
    setAppUserSearch('');
    setAppUserPage([]);
    setAppUserMatchCount(0);
    setAppUserSearchError(null);
    // The picker waits on the project's users; retry if that load failed
    if (!usersLoaded && !loading) loadUsers();
    setAddDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (user: ProjectUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      role: user.role,
      is_default_project: user.is_default_project,
    });
    setEditDialogOpen(true);
  };

  // Open delete confirmation
  const handleOpenDelete = (user: ProjectUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Add user to project
  const handleAddUser = async () => {
    if (!selectedAppUser) {
      setError('Please select a user');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`/projects/${projectId}/users`, {
        email: selectedAppUser.email.toLowerCase(),
        role: formData.role,
        is_default_project: formData.is_default_project,
      });

      setSuccess(`User ${selectedAppUser.full_name || selectedAppUser.email} added to project`);
      setAddDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  // Update user role
  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put(`/projects/${projectId}/users/${selectedUser.user_id}/role`, {
        role: formData.role,
      });

      setSuccess(`Role updated for ${selectedUser.email}`);
      setEditDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  // Remove user from project
  const handleRemoveUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.delete(`/projects/${projectId}/users/${selectedUser.user_id}`);

      setSuccess(`User ${selectedUser.email} removed from project`);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  // Resend invitation to user who hasn't set up their account
  const handleResendInvitation = async (user: ProjectUser) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`/projects/${projectId}/users/${user.user_id}/resend-invitation`);
      setSuccess(`Invitation resent to ${user.email}. Link expires in 48 hours.`);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // A user is pending setup only if they have an unaccepted invitation,
  // haven't set their password, AND have never accessed the project.
  const isPendingSetup = (u: ProjectUser) =>
    !u.password_set_at && !u.last_accessed_at && u.invitation_status && u.invitation_status !== 'accepted';

  // Stats data
  const externalCount = users.filter(u => u.is_external_collaborator).length;
  const pendingSetupCount = users.filter(isPendingSetup).length;
  const stats = [
    {
      label: 'Total Users',
      value: users.length,
      icon: <GroupIcon />,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1),
    },
    {
      label: 'Admins',
      value: users.filter(u => u.role === 'admin').length,
      icon: <SecurityIcon />,
      color: theme.palette.error.main,
      bgColor: alpha(theme.palette.error.main, 0.1),
    },
    {
      label: 'Active Users',
      value: users.filter(u => u.is_active && !isPendingSetup(u)).length,
      icon: <ActiveIcon />,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1),
    },
    ...(pendingSetupCount > 0 ? [{
      label: 'Pending Setup',
      value: pendingSetupCount,
      icon: <PendingIcon />,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.1),
    }] : []),
    ...(externalCount > 0 ? [{
      label: 'External',
      value: externalCount,
      icon: <ExternalIcon />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1),
    }] : []),
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: 4,
      }}>
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ mb: 0.5 }}
          >
            Manage Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {projectName}
          </Typography>
        </Box>
        <IconButton
          onClick={onBack}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 4 }}
      >
        {stats.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: stat.bgColor,
                color: stat.color,
              }}
            >
              {stat.icon}
            </Box>
            <Box>
              <Typography
                variant="h4"
                fontWeight={600}
                sx={{ lineHeight: 1.2 }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {stat.label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Action Bar */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Team Members
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={loadUsers}
              disabled={loading}
              size="small"
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            size="small"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Add User
          </Button>
        </Stack>
      </Box>

      {/* Users Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableCell sx={{ fontWeight: 600, py: 2 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2 }}>Added</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2 }}>Last Active</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ color: 'text.secondary' }}>
                    <GroupIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography>No users have access to this project</Typography>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAdd}
                      sx={{ mt: 1 }}
                    >
                      Add the first user
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.access_id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: getAvatarColor(user.email),
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(user.full_name, user.email)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.full_name || user.email.split('@')[0]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        {user.is_default_project && (
                          <Chip
                            label="Default"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {user.is_external_collaborator && (
                          <Tooltip title="User from a different organization">
                            <Chip
                              icon={<ExternalIcon sx={{ fontSize: '0.7rem !important' }} />}
                              label="External"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Chip
                      icon={getRoleIcon(user.role)}
                      label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      size="small"
                      color={getRoleColor(user.role)}
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    {isPendingSetup(user) ? (
                      <Chip
                        icon={<PendingIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label="Pending Setup"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    ) : (
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.is_active ? 'success' : 'default'}
                        variant={user.is_active ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Typography variant="body2">
                      {formatDate(user.granted_at)}
                    </Typography>
                    {user.granted_by && (
                      <Typography variant="caption" color="text.secondary">
                        by {user.granted_by.name || user.granted_by.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(user.last_accessed_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2.5 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {isPendingSetup(user) && (
                        <Tooltip title="Resend Invitation (48hr link)">
                          <IconButton
                            size="small"
                            onClick={() => handleResendInvitation(user)}
                            disabled={loading}
                            sx={{
                              color: 'warning.main',
                              '&:hover': { color: 'warning.dark', bgcolor: alpha(theme.palette.warning.main, 0.1) }
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit Role">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(user)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Access">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDelete(user)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Add User to Project</Typography>
          <Typography variant="body2" color="text.secondary">
            Grant an existing App User access to {projectName}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Autocomplete
              options={appUserOptions}
              loading={loadingAppUsers || !usersLoaded}
              value={selectedAppUser}
              onChange={(_, newValue) => setSelectedAppUser(newValue)}
              onInputChange={(_, value, reason) => {
                // 'reset' fires on pick and on blur, setting the input to the
                // picked user's label or clearing it. Neither is a search term.
                setAppUserSearch(reason === 'reset' ? '' : value);
              }}
              filterOptions={(options) => options}
              noOptionsText={
                appUserSearchError
                  ? 'Could not load users'
                  : appUserPageTruncated
                    ? 'These matches already have access'
                    : appUserSearch.trim() ? 'No matching users' : 'No available users'
              }
              getOptionLabel={(option) => `${option.full_name || option.email.split('@')[0]} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User"
                  placeholder="Search by name or email..."
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: 2 },
                    endAdornment: (
                      <>
                        {loadingAppUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  error={Boolean(appUserSearchError)}
                  helperText={appUserHelperText}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ py: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: getAvatarColor(option.email),
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(option.full_name, option.email)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.full_name || option.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => handleInputChange('role', e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography fontWeight={500}>{role.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={loading || !selectedAppUser}
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Update User Role</Typography>
          <Typography variant="body2" color="text.secondary">
            Change role for {selectedUser?.full_name || selectedUser?.email}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={(e) => handleInputChange('role', e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box sx={{ py: 0.5 }}>
                    <Typography fontWeight={500}>{role.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {role.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateRole}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Remove Access</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Alert
            severity="warning"
            sx={{ borderRadius: 2 }}
            icon={<DeleteIcon />}
          >
            <Typography variant="body2">
              Are you sure you want to remove <strong>{selectedUser?.full_name || selectedUser?.email}</strong> from this project?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              They will no longer be able to access {projectName}.
            </Typography>
          </Alert>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveUser}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectUsers;
